import { supabase } from "../../supabase";
import {
  applyOrganizationScopeForActor,
  resolveOrganizationIdForQueries,
} from "../orgQueryScope";
import { resolvePayrollTypeDisplayLabel } from "../payrollHelpers";
import { dualWriteUserRate } from "../userRate";

/** System key for User Rate — personal member field, not a matrix row. */
export const USER_RATE_PAYROLL_TYPE = "Standard";

/** Matrix-driven payroll types editable in Admin payroll rates. */
export const MATRIX_PAYROLL_TYPE_KEYS = Object.freeze([
  "Residential",
  "Commercial",
  "Industrial",
]);

export function isUserRatePayrollType(payrollType) {
  return payrollType === USER_RATE_PAYROLL_TYPE;
}

export function isMatrixPayrollTypeKey(payrollType) {
  return MATRIX_PAYROLL_TYPE_KEYS.includes(payrollType);
}

/** Admin dropdown options: prefer org catalog label; never show raw system keys. */
export function toAdminPayrollTypeOptions(catalogOptions = []) {
  return (catalogOptions || []).map((option) => {
    if (!option?.value) {
      return option;
    }

    return {
      ...option,
      label: resolvePayrollTypeDisplayLabel(option.value, option.label),
    };
  });
}

/** Matrix editor options only — excludes User Rate and Non-CCQ session key. */
export function toAdminMatrixPayrollTypeOptions(catalogOptions = []) {
  return toAdminPayrollTypeOptions(catalogOptions).filter(
    (option) => !option?.value || isMatrixPayrollTypeKey(option.value),
  );
}

export async function listPayrollRates(actor, client = supabase) {
  if (!actor) {
    return [];
  }

  let query = client.from("payroll_rates").select("*").order("classification_name");

  query = await applyOrganizationScopeForActor(query, actor, client, {
    tableName: "payroll_rates",
  });

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Upsert a matrix payroll rate for the actor's organization.
 * organization_id is always derived from the authenticated actor — never from
 * a browser-supplied org id.
 *
 * Conflict target: trade_id,classification_name,payroll_type
 * (matches payroll_rates_trade_classification_type_unique; trade_ids are
 * org-owned UUIDs so this is effectively tenant-scoped).
 */
export async function savePayrollRateMatrix(
  { trade_id, classification_name, payroll_type, hourly_rate },
  actor,
  client = supabase,
) {
  if (!actor) {
    throw new Error("Signed-in administrator required.");
  }

  if (!trade_id) {
    throw new Error("Select a trade.");
  }

  if (!isMatrixPayrollTypeKey(payroll_type)) {
    throw new Error(
      "Select a matrix payroll type (Residential, Commercial, or Industrial).",
    );
  }

  const rate = Number(hourly_rate);
  if (!Number.isFinite(rate) || rate <= 0) {
    throw new Error("Enter a valid hourly rate.");
  }

  const organizationId = await resolveOrganizationIdForQueries(actor, client);
  if (!organizationId) {
    throw new Error("organization_id is required for payroll rate save.");
  }

  const { error } = await client.from("payroll_rates").upsert(
    {
      trade_id,
      classification_name,
      payroll_type,
      hourly_rate: rate,
      organization_id: organizationId,
    },
    {
      onConflict: "trade_id,classification_name,payroll_type",
      ignoreDuplicates: false,
    },
  );

  if (error) {
    throw error;
  }

  return { organization_id: organizationId };
}

/**
 * Save User Rate (Phase 1 dual-write).
 * Writes organization_members.hourly_rate and users.hourly_rate.
 * Requires a positive rate (Admin User Rate editor).
 * @see dualWriteUserRate
 */
export async function saveCustomEmployeeRate(
  { supabase_user_id, hourly_rate, organization_id = null },
  client = supabase,
) {
  if (!supabase_user_id) {
    throw new Error("Select an employee.");
  }

  const rate = Number(hourly_rate);
  if (!rate) {
    throw new Error("Enter a valid hourly rate.");
  }

  return dualWriteUserRate(
    {
      supabaseUserId: supabase_user_id,
      hourlyRate: rate,
      organizationId: organization_id,
    },
    client,
  );
}

/**
 * Matrix viewer rows only. Excludes User Rate (Standard) and orphaned Custom
 * matrix cells — Non-CCQ sessions resolve the Residential matrix amount.
 */
export function filterPayrollRatesForLookup(rates, tradeId, classificationName) {
  return (rates || []).filter(
    (rate) =>
      rate.trade_id === tradeId &&
      rate.classification_name === classificationName &&
      isMatrixPayrollTypeKey(rate.payroll_type),
  );
}
